import { PaymentDuesRepository } from "@/repositories/payment-dues.repository";
import type { Payout } from "@shyne/shared/api";
import { IndividualPaymentDue, FullPaymentDueReport, PaymentDueReport, PaymentDuesFilters, PaymentScope, SubsPaymentDue } from "./payment-dues.entity";
import {
  createPaymentSheet,
  type CreatePaymentSheetPayload,
  type CreatePaymentSheetResponse,
} from "@/services/stripe-subs-payments.service";
import {
  finalizeStripePaymentDue,
  type FinalizeStripePaymentDueResponse,
} from "@/services/payment-dues-functions.service";
import {
  payWithSavedMethod,
  type PayWithSavedMethodPayload,
  type PayWithSavedMethodResponse,
} from "@/services/stripe-saved-method-payment.service";
import {
  disableAutopay,
  enableAutopay,
  getAutopayPreview,
  getAutopayStates,
  type AutopayPreview,
  type AutopayRecipientType,
  type RecipientAutopayState,
} from "@/services/vendor-autopay.service";

export type VendorPaymentDues = { individual: IndividualPaymentDue[]; subs: SubsPaymentDue[] };

export async function getVendorPaymentDues(filters?: PaymentDuesFilters): Promise<VendorPaymentDues> {
  return PaymentDuesRepository.getPaymentDuesForVendor({
    fromDate: filters?.fromDate ?? undefined,
    toDate: filters?.toDate ?? undefined,
  });
}

export async function updatePaymentTerms(
  recipientType: "individual" | "subs",
  recipientId: string,
  paymentTermsDays: number
): Promise<void> {
  return PaymentDuesRepository.updatePaymentTerms(recipientType, recipientId, paymentTermsDays);
}

export async function markIndividualPaymentDuesAsPaid(
  individualId: string,
  filters?: Pick<PaymentDuesFilters, "fromDate" | "toDate">,
  paymentScope?: PaymentScope
): Promise<void> {
  const dateRange = {
    fromDate: filters?.fromDate ?? undefined,
    toDate: filters?.toDate ?? undefined,
  };
  return PaymentDuesRepository.markDuesPaid("individual", individualId, dateRange, paymentScope);
}

export async function markSubsPaymentDuesAsPaid(
  subOrgId: string,
  filters?: Pick<PaymentDuesFilters, "fromDate" | "toDate">,
  paymentScope?: PaymentScope
): Promise<void> {
  const dateRange = {
    fromDate: filters?.fromDate ?? undefined,
    toDate: filters?.toDate ?? undefined,
  };
  return PaymentDuesRepository.markDuesPaid("subs", subOrgId, dateRange, paymentScope);
}

export async function getIndividualPaymentDueReport(
  individualId: string,
  filters?: Pick<PaymentDuesFilters, "fromDate" | "toDate">
): Promise<PaymentDueReport> {
  const dateRange = {
    fromDate: filters?.fromDate ?? undefined,
    toDate: filters?.toDate ?? undefined,
  };
  return PaymentDuesRepository.getIndividualPaymentDueReport(individualId, dateRange);
}

export async function getSubsPaymentDueReport(
  subOrgId: string,
  filters?: Pick<PaymentDuesFilters, "fromDate" | "toDate">
): Promise<PaymentDueReport> {
  const dateRange = {
    fromDate: filters?.fromDate ?? undefined,
    toDate: filters?.toDate ?? undefined,
  };
  return PaymentDuesRepository.getSubsPaymentDueReport(subOrgId, dateRange);
}

export async function createStripePaymentForSubsDue(
  payload: CreatePaymentSheetPayload
): Promise<CreatePaymentSheetResponse> {
  return createPaymentSheet(payload);
}

export async function createStripePaymentForIndividualDue(
  payload: CreatePaymentSheetPayload
): Promise<CreatePaymentSheetResponse> {
  return createPaymentSheet(payload);
}

export async function finalizeStripePaymentForSubsDue(
  subsOrgId: string,
  paymentIntentId: string,
  filters?: Pick<PaymentDuesFilters, "fromDate" | "toDate">,
  paymentScope?: PaymentScope
): Promise<FinalizeStripePaymentDueResponse> {
  const dateRange = {
    fromDate: filters?.fromDate ?? undefined,
    toDate: filters?.toDate ?? undefined,
  };

  return finalizeStripePaymentDue({
    kind: "due",
    recipientType: "subs",
    recipientId: subsOrgId,
    paymentIntentId,
    dateRange,
    paymentScope,
  });
}

export async function finalizeStripePaymentForIndividualDue(
  individualId: string,
  paymentIntentId: string,
  filters?: Pick<PaymentDuesFilters, "fromDate" | "toDate">,
  paymentScope?: PaymentScope
): Promise<FinalizeStripePaymentDueResponse> {
  const dateRange = {
    fromDate: filters?.fromDate ?? undefined,
    toDate: filters?.toDate ?? undefined,
  };

  return finalizeStripePaymentDue({
    kind: "due",
    recipientType: "individual",
    recipientId: individualId,
    paymentIntentId,
    dateRange,
    paymentScope,
  });
}

export async function payDueWithSavedMethod(
  payload: PayWithSavedMethodPayload
): Promise<PayWithSavedMethodResponse> {
  return payWithSavedMethod(payload);
}

export async function createStripePaymentForExtraPayment(
  payload: CreatePaymentSheetPayload
): Promise<CreatePaymentSheetResponse> {
  return createPaymentSheet(payload);
}

export async function finalizeStripePaymentForExtraPayment({
  recipientType,
  recipientId,
  amount,
  description,
  paymentIntentId,
}: {
  recipientType: "individual" | "subs";
  recipientId: string;
  amount: number;
  description: string;
  paymentIntentId: string;
}): Promise<FinalizeStripePaymentDueResponse> {
  const result = await finalizeStripePaymentDue({
    kind: "extra",
    recipientType,
    recipientId,
    paymentIntentId,
    amount,
    description,
  });

  if (!result.payoutId) throw new Error("Failed to create extra payment payout record");
  return result;
}

export async function getIndividualFullItemReport(
  individualId: string,
  filters?: Pick<PaymentDuesFilters, "fromDate" | "toDate">
): Promise<FullPaymentDueReport> {
  const dateRange = {
    fromDate: filters?.fromDate ?? undefined,
    toDate: filters?.toDate ?? undefined,
  };
  return PaymentDuesRepository.getIndividualFullItemReport(individualId, dateRange);
}

export async function getSubsFullItemReport(
  subOrgId: string,
  filters?: Pick<PaymentDuesFilters, "fromDate" | "toDate">
): Promise<FullPaymentDueReport> {
  const dateRange = {
    fromDate: filters?.fromDate ?? undefined,
    toDate: filters?.toDate ?? undefined,
  };
  return PaymentDuesRepository.getSubsFullItemReport(subOrgId, dateRange);
}

export async function getPaymentHistory(recipientId: string): Promise<Payout[]> {
  return PaymentDuesRepository.getPaymentHistory(recipientId);
}

export async function getRecipientAutopayStates(): Promise<RecipientAutopayState[]> {
  return getAutopayStates();
}

export async function getRecipientAutopayPreview(
  recipientType: AutopayRecipientType,
  recipientId: string
): Promise<AutopayPreview> {
  return getAutopayPreview(recipientType, recipientId);
}

export async function enableRecipientAutopay(
  recipientType: AutopayRecipientType,
  recipientId: string
): Promise<RecipientAutopayState> {
  return enableAutopay(recipientType, recipientId);
}

export async function disableRecipientAutopay(
  recipientType: AutopayRecipientType,
  recipientId: string
): Promise<RecipientAutopayState> {
  return disableAutopay(recipientType, recipientId);
}
